/*! ravelinjs 1.8.1 - https://github.com/unravelin/ravelinjs. Copyright 2023 */
(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? module.exports = factory() :
  typeof define === 'function' && define.amd ? define(factory) :
  (global = typeof globalThis !== 'undefined' ? globalThis : global || self, global.Ravelin = factory());
})(this, (function () { 'use strict';

  /**
   * CookieJar provides getters/setters for cookies.
   * @class
   * @param {object} cfg The cookiejar configuration.
   * @param {string} [cfg.domain] The domain on which to store cookies.
   */
  function CookieJar(cfg) {
    this.domain = cfg.domain;
  }

  /**
   * get returns the value of the cookie of the given name, or undefined.
   * @param {string} name
   * @returns {string|undefined}
   */
  CookieJar.prototype.get = function(name) {
    var cookies = document.cookie.split('; ');
    for (var i = cookies.length-1; i >= 0; i--) {
      var x = cookies[i].split("=");
      if (x[0] === name) {
        return x[1];
      }
    }
    return undefined;
  };

  /**
   * set a cookie on the given domain.
   * @param {object} c The cookie to set.
   * @param {string} c.name The name of the cookie.
   * @param {string} c.value The value to be stored in the cookie.
   * @param {string} [c.path="/"] The path to store the cookie under.
   * @param {Date} [c.expires] The expiry time of the cookie. If not set, the cookie implicitly becomes a session cookie.
   */
  CookieJar.prototype.set = function(c) {
    var cook = c.name + "=" + c.value + ";path=" + (c.path || "/") +
      (c.expires ? ";expires=" + c.expires.toUTCString() : "") +
      (this.domain ? ";domain=" + this.domain : "");

    if (typeof(this.samesite) === 'undefined') {
      // Determine the correct value for SameSite.
      if (hasWebKitSameSiteBug(window.navigator.userAgent)) {
        this.samesite = '';
      } else if (window.location.protocol == 'https:') {
        this.samesite = ';SameSite=None;Secure';
      } else {
        this.samesite = ';SameSite=Lax';
      }

      // Try this samesite value.
      document.cookie = cook + this.samesite;
      if (this.get(c.name) == c.value) {
        // We set the cookie successfully.
        return;
      }
      // The cookie was discarded - avoid using samesite.
      this.samesite = '';
    }

    document.cookie = cook + this.samesite;
  };

  /**
   * hasWebKitSameSiteBug indicates whether a given User-Agent treats
   * SameSite:None on cookies as if it were SameSite:Strict, which is very
   * different. Adapted from the procedure on
   * https://www.chromium.org/updates/same-site/incompatible-clients.
   *
   * @param ua string
   * @returns bool
   */
  function hasWebKitSameSiteBug(ua) {
    if (/\(iP.+; CPU .*OS 12[_\d]*.*\) AppleWebKit\//.test(ua)) {
      return true;
    }
    if (/\(Macintosh;.*Mac OS X 10_14[_\d]*.*\) AppleWebKit\//.test(ua)) {
      if (/^Mozilla\/[\.\d]+ \(Macintosh;.*Mac OS X [_\d]+\) AppleWebKit\/[\.\d]+ \(KHTML, like Gecko\)$/.test(ua)) {
        return true;
      }
      if (/Version\/.* Safari\//.test(ua) && !/Chrom(e|ium)/.test(ua)) {
        return true;
      }
    }
    return false;
  }

  /**
   * @private
   * @module ravelinjs/util
   */

  /**
   * bind wraps a function.
   * @param {Function} fn The function to be called.
   * @param {*} thisArg The 'this' of the invoked fn.
   * @param {...*} [args] Arguments to add to the start of the function call.
   * @returns {Function}
   */
  function bind(fn, thisArg, args) {
    args = Array.prototype.slice.call(arguments, 2);
    if (!Function.prototype.bind) {
      return function () {
        return fn.apply(thisArg, args.concat(Array.prototype.slice.call(arguments)));
      };
    }
    return Function.prototype.bind.apply(fn, [thisArg].concat(args));
  }

  /**
   * A lookup table used for uuid generation. Populated on first usage.
   * @type {string[]}
   * @private
   */
  var _lut;

  /**
   * uuid generates a fresh [UUID v4](https://en.wikipedia.org/wiki/Universally_unique_identifier#Version_4_(random)).
   *
   * @returns {string}
   */
  function uuid() {
    var d0, d1, d2, d3;

    // try to use the newer, randomer crypto.getRandomValues if available
    if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues && typeof Int32Array !== 'undefined') {
      var d = new Int32Array(4);
      window.crypto.getRandomValues(d);
      d0 = d[0];
      d1 = d[1];
      d2 = d[2];
      d3 = d[3];
    } else {
      // Generate a random float between 0-1, multiple by 4294967295 (0xffffffff)
      // then round down via bitwise or (|0) so we are left with a random 32bit
      // number. These 4 values are then bitshifted around to produce additional
      // random values.
      d0 = Math.random() * 0xffffffff | 0;
      d1 = Math.random() * 0xffffffff | 0;
      d2 = Math.random() * 0xffffffff | 0;
      d3 = Math.random() * 0xffffffff | 0;
    }

    // Populate our lookup table (_lut) sequentially with hexidecimal strings
    // starting from 00 all the way through to ff, covering the entire 256 hex
    // range.
    if (!_lut) {
      _lut = [];
      for (var i = 0; i < 256; i++) {
        _lut[i] = (i < 16 ? '0' : '') + (i).toString(16);
      }
    }

    // From our 4 random 32 bit values, we take the first 8 bits via bitwise AND
    // against 255 (&0xff), then the next 8 bits via bitwise shift right (>>8) and
    // repeat that 4 times through to get 4 random, 8 bit numbers, which are used
    // to look up the sequentially generated hex characters in our lookup table.
    // There are two interesting numbers here though:
    //
    // - the 15th character will always be a 4, because we bitwise AND against 15
    //   rather than 255 and we bitwise OR against 64 (0x40), producing values in
    //   the range of 64-79, which is the 16 hex values prefixed with a 4 (40
    //   through to 4f)
    //
    // - the 20th character will always be one of 8, 9, a or b because we bitwise
    //   AND against 63 and bitwise OR against 128, producing values in the range
    //   of 128-191, which is the 64 hex values ranging from 80 through to bf
    //
    // This logic almost mirrors the specification of v4 RFC 4122 UUIDs, but omits
    // the `clock_seq_hi_and_reserved` requirement
    // https://tools.ietf.org/html/rfc4122.
    //
    // The result are identifiers of 36 characters, 34 of which are randomly
    // assigned.
    return _lut[d0 & 0xff] + _lut[d0 >> 8 & 0xff] + _lut[d0 >> 16 & 0xff] + _lut[d0 >> 24 & 0xff] + '-' +
      _lut[d1 & 0xff] + _lut[d1 >> 8 & 0xff] + '-' + _lut[d1 >> 16 & 0x0f | 0x40] + _lut[d1 >> 24 & 0xff] + '-' +
      _lut[d2 & 0x3f | 0x80] + _lut[d2 >> 8 & 0xff] + '-' + _lut[d2 >> 16 & 0xff] + _lut[d2 >> 24 & 0xff] +
      _lut[d3 & 0xff] + _lut[d3 >> 8 & 0xff] + _lut[d3 >> 16 & 0xff] + _lut[d3 >> 24 & 0xff];
  }

  /**
   * promiseRetry calls factory up to retries+1 times until the returned promise
   * resolves.
   * @param {PromiseConstructor} P
   * @param {function(): Promise} factory
   * @param {number} [retries=2]
   * @param {number} [retryBackoffMs=150]
   */
  function promiseRetry(P, factory, retries, retryBackoffMs) {
    if (!retries) retries = 2;
    var n = 0;
    return new P(function (resolve, reject) {
      return attempt(0);
      function attempt(delay) {
        return new P(function (r) {
          if (delay) setTimeout(r, delay); else r();
        })
        .then(function () {
          return factory(function retry(err) {
            throw { message: 'promise-retry', err: err };
          }, ++n);
        })
        .then(
          resolve,
          function (e) {
            if (e && e.message === 'promise-retry') {
              if (n - 1 < retries) {
                return attempt(delay + (retryBackoffMs || 150));
              }
              reject(e.err);
              return;
            }
            reject(e);
          }
        );
      }
    });
  }

  /**
   * The core module of ravelinjs provides device-identification, and is used by
   * other modules to communicate with the Ravelin API.
   */

  /**
   * @typedef {object} CoreConfig
   * @prop {string} [key] The API key ("publishable_key_..." or "pk_...") used to authenticate with the Ravelin API.
   * @prop {string} [api] A string of the format "https://api.ravelin.net" which forms the base of API requests.
   * @prop {string|Promise<string>} [id] An explicit deviceId to use. If the Promise errors or returns an empty value, Ravelin's own deviceId tracking kicks in.
   * @prop {string} [cookie=ravelinDeviceId] The name of the cookie that the deviceId is kept in.
   * @prop {string} [cookieDomain] The domain on which to set cookies.
   * @prop {number} [cookieExpiryDays=365] The max lifetime of a deviceId, in days. Values <= 0 makes Ravelin treat the named cookie as read-only.
   * @prop {number} [syncMs=2000] The sync timeout frequency.
   * @prop {number} [sendRetryMs=150] The send retry backoff time.
   * @prop {PromiseConstructor} [Promise] The Promise implementation to use in ravelinjs's asynchronous functions.
   */

  /**
   * Core library instance. Provides helpers and device identification.
   * @class
   * @param {CoreConfig} cfg
   */
  function Core(cfg) {
    this.Promise = cfg.Promise;

    // API connectivity.
    this.version = cfg.version || "1.8.1-ravelinjs";
    this.key = cfg.key;
    this.api = cfg.api || apiFromKey(this.key);
    this.api = this.api[0] + this.api.substr(1).replace(/^\/+|\/$/g, '');
    this.sendRetryMs = cfg.sendRetryMs || 150;
    this.cookie = cfg.cookie || 'ravelinDeviceId';
    this.sessionCookie = cfg.sessionCookie || 'ravelinSessionId';
    this.cookieExpiryDays = cfg.cookieExpiryDays || 365;
    this.cookies = new CookieJar({
      domain: cfg.cookieDomain
    });

    this.prefix = typeof cfg.prefix === 'string' ? cfg.prefix : 'rjs-';
    this._id = resolve(this.Promise, cfg.id);
    this.sync();
    if (cfg.init !== false) {
      this.attach(cfg.syncMs || 2000);
    }
  }

  /**
   * resolve returns a Promise that resolves to v. If v is already a promise, we
   * just return it.
   * @param {PromiseConstructor} Promise The promise implementation.
   * @param {Promise|any} v The value we want in a then.
   * @returns {Promise}
   */
  function resolve(Promise, v) {
    if (v && typeof(v.then) === 'function') {
      return v;
    }
    return new Promise(function(r) { r(v); });
  }

  /**
   * id reads our device ID from the browser.
   * @returns {Promise<string>}
   */
  Core.prototype.id = function() {
    return this.ids().then(function(ids) {
      return ids.device;
    });
  };

  /**
   * @typedef {Object} IDs
   * @prop {string} device
   * @prop {string} session
   */

  /**
   * ids reads all IDs from the browser.
   * @returns {Promise<IDs>}
   */
  Core.prototype.ids = function() {
    if (this._ids) {
      return this._ids;
    }

    var c = this;
    this._ids =
      this.sniffError(this._id)
      .then(undefined, function() { /* swallow error. */ })
      .then(function(cfgId) {
        var d = cfgId || c.cookies.get(c.cookie);
        var s = c.cookies.get(c.sessionCookie);

        if (!cfgId && s) {
          // Restore d from s.
          var sep = s.indexOf(':');
          if (sep != -1) {
            d = s.substr(0, sep);
          }

          // Strip d: from the beginning of s.
          if (d && hasPrefix(s, d+':')) {
            s = s.substr(d.length+1);
          }
        }

        return {
          device: d || c.prefix + uuid(),
          session: s || uuid()
        };
      });
    return this._ids;
  };

  /**
   * hasPrefix returns whether string s starts with string prefix.
   * @param {string} s
   * @param {string} prefix
   * @returns {boolean}
   */
  function hasPrefix(s, prefix) {
    return s.substr(0, prefix.length) === prefix;
  }

  /**
   * daysFromNow returns the current time, days in the future.
   * @param {number} days The number of days in the future.
   * @returns Date
   */
  function daysFromNow(days) {
    return new Date(new Date().getTime() + days * 86400 * 1000);
  }

  /**
   * sync writes our ID into the various places we try to keep hold of it.
   */
  Core.prototype.sync = function() {
    var c = this;
    return this.ids().then(function(ids) {
      if (c.cookieExpiryDays > 0) {
        c.cookies.set({
          name: c.cookie,
          value: ids.device,
          expires: daysFromNow(c.cookieExpiryDays)
        });
      }
      c.cookies.set({
        name: c.sessionCookie,
        value: ids.device + ":" + ids.session
      });
    });
  };

  /**
   * attach to the browser to maintain our ids somewhere.
   * @param {number} syncMs How often we attempt to re-synchronise.
   */
  Core.prototype.attach = function(syncMs) {
    setInterval(bind(this.sync, this), syncMs);
  };

  /**
   * sameOrigin returns whether a url is treated as the same origin of the page
   * loaded into the browser.
   * @param {string} url
   * @returns {boolean}
   */
  function sameOrigin(url) {
    var matches = url.match('(https?)?://([^/]+)');
    if (!matches) {
      // Relative paths must be the same origin.
      return true;
    }
    // Check the host:port matches.
    if (matches[2] != window.location.host) {
      return false;
    }
    // Check the scheme matches.
    if (matches[1] != "" && matches[1] != window.location.protocol.replace(/:/, '')) {
      return false;
    }
    return true;
  }

  var defaultAPI = 'https://live.ravelin.click';

  /**
   * apiFromKey returns the likely API URL based on the key. Keys are arbitrary
   * strings, but we occassionally issue keys of the format
   * "publishable_key_[test_][env_][key]" to embed env.
   * @param {string} key
   * @returns {string} API URL
   */
  function apiFromKey(key) {
    if (!key || key.substr(0, 16) != 'publishable_key_') {
      return defaultAPI;
    }

    var words = key.substr(16).split('_');
    if (words.length < 2) {
      return defaultAPI;
    }

    var env = words[words.length - 2];
    if (env == 'test' || env == 'live') {
      return defaultAPI;
    }

    return 'https://' + encodeURIComponent(env) + '.ravelin.click';
  }

  var _XDomainRequest = typeof XDomainRequest !== 'undefined' && XDomainRequest;

  /**
   * @typedef {Object} CoreResponse
   * @property {number} status
   * @property {string} text
   * @property {number} attempt
   */

  /**
   * reportError sends an Error to Ravelin so that we can track what's happening.
   * @param {*} e
   * @returns {Promise<void>}
   */
  Core.prototype.reportError = function(e) {
    var that = this;
    if (this._ids) {
      return this._ids
        .then(undefined, function() { /* swallow error. */ })
        .then(report);
    }
    return report();

    function report(ids) {
      return that.send('POST', 'z/err', {
        deviceId: ids && ids.device,
        libVer: that.version,
        type: e.name,
        msg: e.message || e,
        error: e.stack
      }).then(function() {});
    }
  };

  /**
   * sniffError invokes fn and reports any errors back to Ravelin. If the return
   * value of fn is a Promise-like object then any rejection is also reported.
   */
  Core.prototype.sniffError = function(fn) {
    var c = this;
    try {
      var p = typeof fn === 'function' ? fn() : fn;
      if (p && p.then) p.then(undefined, function(e) {
        c.reportError(e);
      });
      return p;
    } catch (e) {
      c.reportError(e);
      throw e;
    }
  };

  /**
   * bind returns a function that invokes fn.apply(thisArg, ...args) and reports
   * any errors using sniffError.
   *
   * @param {Function} fn
   * @param {*} [thisArg]
   * @param {...*} [args]
   * @returns {Function}
   */
  Core.prototype.bind = function() {
    var c = this;
    var fn = bind.apply(null, Array.prototype.slice.call(arguments));
    return function() {
      var args = Array.prototype.slice.call(arguments);
      return c.sniffError(function() {
        return fn.apply(null, args);
      });
    };
  };

  /**
   * send makes a HTTP request and returns a Promise that resolves to the response
   * body or is rejected with any errors or non-2xx status codes.
   *
   * For backwards compatibility with IE8-9 whose XDomainRequest does not allow us
   * to add headers to cross-domain requests - and to avoid OPTIONS preflight
   * requests on browsers that do - we do not accept setting headers here. Instead
   * we put the key in the query string and expect the server to support whatever
   * headers the browser cares to add.
   *
   * @param {string} method
   * @param {string} path
   * @param {object} body
   * @returns {Promise<CoreResponse>}
   */
  Core.prototype.send = function(method, path, body) {
    if (!this.key) {
      throw new Error('ravelin/core: no key set for API requests');
    }

    // Build the URL to run.
    var url = this.api + '/' + path.replace(/^\/+/, '');
    url += (url.indexOf('?') == -1 ? '?' : '&') + 'key=' + encodeURIComponent(this.key);
    url = url.replace(/^\/+/, '/');

    var c = this;
    return promiseRetry(c.Promise, function(retry, attempt) {
      return (
        // Make the request.
        !sameOrigin(url) && _XDomainRequest) ?
          c._sendXDR(method, url, stringify(body)) :
          c._sendXHR(method, url, stringify(body)
      ).then(function(r) {
        r.attempt = attempt;

        // Resolve/reject based on status.
        if (r.status == 1223) {
          r.status = 204; // IE0013 from http://www.enhanceie.com/ie/bugs.asp.
        }
        if (r.status >= 200 && r.status < 300) {
          return r;
        } else if (r.status == 400 && r.text == "") {
          // Retry empty 400s injected by the BrowserStack tunnel.
          retry(r);
        } else if (r.status >= 500) {
          // Retry errors.
          retry(r);
        } else if (!r.status) {
          // Retry connection errors.
          retry(r);
        }
        return new c.Promise(function(resolve, reject) {
          reject(r);
        });
      });
    }, 2, this.sendRetryMs)
    .then(undefined, function(r) {
      if (r.attempt) {
        // Convert the response format into an error.
        throw new Error("ravelin/core: " + method + " " + url + " attempt " + r.attempt + " returned status " + r.status + (r.text ? " and body " + r.text : ''));
      }
      throw r;
    });
  };

  /**
   * _sendXHR wraps an XHR in a Promise.
   *
   * @param {string} method
   * @param {string} url
   * @param {object} body
   * @returns {Promise<CoreResponse>}
   */
  Core.prototype._sendXHR = function(method, url, body) {
    return new this.Promise(function(resolve) {
      var r = new XMLHttpRequest();
      r.onreadystatechange = function() {
        if (r.readyState == 4) {
          resolve({status: r.status, text: r.responseText});
        }
      };

      // Send the request.
      r.open(method, url);
      r.send(body);
    });
  };

  /**
   * _sendXDR wraps an XHR in a Promise.
   *
   * @param {string} method
   * @param {string} url
   * @param {object} body
   * @returns {Promise<CoreResponse>}
   */
  Core.prototype._sendXDR = function(method, url, body) {
    return new this.Promise(function(resolve, reject) {
      var r = new _XDomainRequest();
      r.onload = function() {
        resolve({status: r.status || 200, text: r.responseText});
      };
      r.onerror = function() {
        reject({status: r.status, text: r.responseText});
      };
      r.onprogress = function() {
        // Needed to ensure onload fires.
      };

      r.open(method, url);
      setTimeout(function () {
        // Wrapped in a timeout to prevent an issue with the interface where some
        // requests are lost if multiple XDomainRequests are being sent at the
        // same time.
        // https://developer.mozilla.org/en-US/docs/Web/API/XDomainRequest.
        r.send(body);
      }, 0);
    });
  };

  /**
   * stringify is JSON.stringify with prototype safety.
   *
   * @param {object} obj
   * @returns {string}
   */
  function stringify(obj) {
    if (Array.prototype.toJSON) {
      // https://stackoverflow.com/questions/710586/json-stringify-array-bizarreness-with-prototype-js
      var _array_tojson = Array.prototype.toJSON;
      delete Array.prototype.toJSON;
      var str = JSON.stringify(obj);
      /* jshint -W121 */// We're restoring a native extension.
      Array.prototype.toJSON = _array_tojson;
      /* jshint +W121 */
      return str;
    }
    return JSON.stringify(obj);
  }

  /**
   * @typedef {object} TrackConfig
   * @prop {boolean} [init=true] False disables hooking into the browser.
   * @prop {boolean} [track=true] False disables hooking into the browser.
   * @prop {Object|boolean} [page] Additional properties describing the page,
   * passed to load(page). If false, disables automatic page tracking.
   */

  /**
   * @class
   * @param {Core} core
   * @param {TrackConfig} [cfg]
   */
  function Track(core, cfg) {
    this.core = core;

    /** @prop {string} windowId An identifier which lasts until the next page refresh. */
    this.windowId = uuid();

    // Paste events
    this.classifyPaste = cfg.classifyPaste;

    try {
      this.dimensions = windowDimensions();
    } catch(e) {}

    // Start tracking events.
    if (cfg.init !== false && cfg.track !== false) {
      this.init(cfg.page);
    }
  }

  /**
   * init hooks Track into the page.
   * @param {Object|boolean} page Additional properties describing the page,
   * passed to load(page). If false, disables automatic page tracking.
   */
  Track.prototype.init = function(page) {
    if (page !== false) {
      this.load(page);
    }
    this._attachAll();
  };

  /**
   * _attachAll event listeners at the root of the document to return some key
   * events back to Ravelin.
   */
  Track.prototype._attachAll = function() {
    this._listeners = [];

    if (document.addEventListener) {
      this._attach({
        target: document,
        addFn: 'addEventListener',
        delFn: 'removeEventListener',
        event: 'paste',
        handler: this.core.bind(this.paste, this)
      });
    }
    if (window.addEventListener) {
      this._attach({
        target: window,
        addFn: 'addEventListener',
        delFn: 'removeEventListener',
        event: 'resize',
        handler: debounce(250, this.core.bind(this.resize, this))
      });
    } else if (window.attachEvent) {
      this._attach({
        target: window,
        addFn: 'attachEvent',
        delFn: 'detachEvent',
        event: 'onresize',
        handler: debounce(250, this.core.bind(this.resize, this))
      });
    }
  };

  /**
   * _attach mirrors target.addEventListener(event, handler) where addFn would be
   * 'addEventListener' and delFn 'removeEventListener'.
   *
   * @param {object} e
   * @param {object} e.target
   * @param {string} e.addFn
   * @param {string} e.delFn
   * @param {string} e.event
   * @param {Function} e.handler
   */
  Track.prototype._attach = function(e) {
    this._listeners.push(e);
    var fn = e.target[e.addFn];
    if (fn.call) {
      return fn.call(e.target, e.event, e.handler);
    }
    return fn(e.event, e.handler);
  };

  /**
   * _detach removes event listeners that we added.
   */
  Track.prototype._detach = function() {
    while (this._listeners && this._listeners.length > 0) {
      var e = this._listeners.pop();
      var fn = e.target[e.delFn];
      if (fn.call) {
        fn.call(e.target, e.event, e.handler);
      } else {
        fn(e.event, e.handler);
      }
    }
  };

  /**
   * send an event to Ravelin.
   * @param {string} type Identifies the origin of the event.
   * @param {string} name A description of what occurred.
   * @param {object} [props]
   */
  Track.prototype._send = function(type, name, props) {
    var that = this;
    return this.core.ids().then(function(ids) {
      return that.core.send('POST', '/z', {events: [{
        eventType: type,
        eventData: {
          eventName: name,
          properties: props
        },

        libVer: that.core.version,
        eventMeta: {
          trackingSource: 'browser',
          url: window.location.href,
          ravelinDeviceId: ids.device,
          ravelinSessionId: ids.session,
          ravelinWindowId: that.windowId,
          pageTitle: document.title,
          referrer: document.referrer || undefined,
          clientEventTimeMilliseconds:  Date.now ? Date.now() : +new Date(),
          timezoneOffset: new Date().getTimezoneOffset()
        }
      }]});
    });
  };

  /**
   * load event handler.
   * @param {object} [props] Additional properties describing the page loaded.
   */
  Track.prototype.load = function(props) {
    return this._send('track', 'PAGE_LOADED', props);
  };

  /**
   * event sends a user-driven event to Ravelin.
   * @param {string} name The name of the event.
   * @param {object} [props] Additional properties to describe the event.
   * @returns
   */
  Track.prototype.event = function(name, props) {
    return this._send('track', name, props);
  };

  /**
   * resize event handler.
   */
  Track.prototype.resize = function() {
    var cur;
    try {
      cur = windowDimensions();
    } catch (e) {
      return;
    }

    // Swap out old sizes.
    var old = this.dimensions;
    this.dimensions = cur;

    if (old.w === cur.w && old.h === cur.h) {
      return;
    }
    this._send('resize', 'resize', {
      resolutionOld: {
        w: old.w,
        h: old.h
      },
      resolutionNew: {
        w: cur.w,
        h: cur.h
      }
    });
  };

  /**
   * windowDimensions returns the size of the browser window, using the first set
   * of properties available to do so.
   *
   * There is a strange behaviour on Firefox (possibly related to extensions?)
   * where this function can throw a "TypeError: can't access dead object" -
   * https://github.com/unravelin/ravelinjs/issues/105.
   *
   * @returns {w:Number, h:Number}
   */
  function windowDimensions() {
    return {
      w: window.outerWidth || window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth,
      h: window.outerHeight || window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
    };
  }

  /**
   * paste event handler.
   * @param {ClipboardEvent} e
   */
  Track.prototype.paste = function(e) {
    var sensitive = false;
    var panField = false;
    var props = {};

    var t = e && e.target;
    if (t) {
      // Customisable logic for identifying pan and sensitive fields
      var custom = this.classifyPaste && this.classifyPaste(e);
      panField = t.hasAttribute('data-rvn-pan') || (custom && custom.pan);

      // Locate the field.
      props.fieldName = t.name || t.id || undefined;
      if (t.form) {
        props.formName = attr(t.form, 'name') || attr(t.form, 'id');
        props.formAction = attr(t.form, 'action');
      }

      // Look at the value, if safe.
      sensitive = t.type == 'password' || treeHasAttr(t, 'data-rvn-sensitive') || (custom && custom.sensitive);
      if (!sensitive) {
        if (t.value) {
          props.fieldValue = obfsPasteData(t.value);
        }
        if (typeof t.selectionStart === 'number') {
          props.selectionStart = t.selectionStart;
          props.selectionEnd = t.selectionEnd;
        }
      }
    }

    // Describe the pasted content, if safe.
    if (!sensitive && e && e.clipboardData) {
      // Don't consider window.clipboardData: it prompts the user.
      var pastedData = e.clipboardData.getData("Text") || e.clipboardData.getData("text/plain");
      if (pastedData) {
        if (panField) props.panCleaned = detectPAN(pastedData);
        props.pastedValue = obfsPasteData(pastedData);
      }
    }
    if (panField && typeof props.panCleaned === 'undefined') {
      props.panCleaned = true;
    }

    return this._send('paste', 'paste', props);
  };



  /**
   * detectPAN returns whether it looks like str contains a card number.
   * @param {string} str
   */
  function detectPAN(str) {
    // Disregard space or dash separators between characters.
    str = str.replace(/[ -]+/g, '');

    // Find each sequence of digits.
    var nums = /\d+/g;
    var match;
    while ((match = nums.exec(str)) !== null) {
      if (match[0].length >= 12 && match[0].length <= 19) {
        // If there was a sequence of digits between 12 or 19 characters long,
        // consider it a PAN.
        return true;
      }
    }
    return false;
  }

  /**
   * obfsPasteData replaces all unicode Letters with X and 0-9 with 0 so that the
   * structure of a paste value is obvious, without revealing the contents.
   * @param {*} str
   */
  function obfsPasteData(str) {
    // This regexp covers all latin and international alphabetic and numberic
    // characters. It is insufficient to replace /\w/g or /\d/g, but /\p{L}/ug
    // will match unicode Letters and /\p{N}/ug unicode Numbers. Generated at:
    // https://mothereff.in/regexpu#input=str.replace%28%2F%5Cp%7BN%7D%2Fgu%2C+%270%27%29.replace%28%2F%5Cp%7BL%7D%2Fgu%2C+%27X%27%29&dotAllFlag=1&unicodePropertyEscape=1
    return str.replace(/(?:[0-9\xB2\xB3\xB9\xBC-\xBE\u0660-\u0669\u06F0-\u06F9\u07C0-\u07C9\u0966-\u096F\u09E6-\u09EF\u09F4-\u09F9\u0A66-\u0A6F\u0AE6-\u0AEF\u0B66-\u0B6F\u0B72-\u0B77\u0BE6-\u0BF2\u0C66-\u0C6F\u0C78-\u0C7E\u0CE6-\u0CEF\u0D58-\u0D5E\u0D66-\u0D78\u0DE6-\u0DEF\u0E50-\u0E59\u0ED0-\u0ED9\u0F20-\u0F33\u1040-\u1049\u1090-\u1099\u1369-\u137C\u16EE-\u16F0\u17E0-\u17E9\u17F0-\u17F9\u1810-\u1819\u1946-\u194F\u19D0-\u19DA\u1A80-\u1A89\u1A90-\u1A99\u1B50-\u1B59\u1BB0-\u1BB9\u1C40-\u1C49\u1C50-\u1C59\u2070\u2074-\u2079\u2080-\u2089\u2150-\u2182\u2185-\u2189\u2460-\u249B\u24EA-\u24FF\u2776-\u2793\u2CFD\u3007\u3021-\u3029\u3038-\u303A\u3192-\u3195\u3220-\u3229\u3248-\u324F\u3251-\u325F\u3280-\u3289\u32B1-\u32BF\uA620-\uA629\uA6E6-\uA6EF\uA830-\uA835\uA8D0-\uA8D9\uA900-\uA909\uA9D0-\uA9D9\uA9F0-\uA9F9\uAA50-\uAA59\uABF0-\uABF9\uFF10-\uFF19]|\uD800[\uDD07-\uDD33\uDD40-\uDD78\uDD8A\uDD8B\uDEE1-\uDEFB\uDF20-\uDF23\uDF41\uDF4A\uDFD1-\uDFD5]|\uD801[\uDCA0-\uDCA9]|\uD802[\uDC58-\uDC5F\uDC79-\uDC7F\uDCA7-\uDCAF\uDCFB-\uDCFF\uDD16-\uDD1B\uDDBC\uDDBD\uDDC0-\uDDCF\uDDD2-\uDDFF\uDE40-\uDE48\uDE7D\uDE7E\uDE9D-\uDE9F\uDEEB-\uDEEF\uDF58-\uDF5F\uDF78-\uDF7F\uDFA9-\uDFAF]|\uD803[\uDCFA-\uDCFF\uDD30-\uDD39\uDE60-\uDE7E\uDF1D-\uDF26\uDF51-\uDF54\uDFC5-\uDFCB]|\uD804[\uDC52-\uDC6F\uDCF0-\uDCF9\uDD36-\uDD3F\uDDD0-\uDDD9\uDDE1-\uDDF4\uDEF0-\uDEF9]|\uD805[\uDC50-\uDC59\uDCD0-\uDCD9\uDE50-\uDE59\uDEC0-\uDEC9\uDF30-\uDF3B]|\uD806[\uDCE0-\uDCF2\uDD50-\uDD59]|\uD807[\uDC50-\uDC6C\uDD50-\uDD59\uDDA0-\uDDA9\uDFC0-\uDFD4]|\uD809[\uDC00-\uDC6E]|\uD81A[\uDE60-\uDE69\uDF50-\uDF59\uDF5B-\uDF61]|\uD81B[\uDE80-\uDE96]|\uD834[\uDEE0-\uDEF3\uDF60-\uDF78]|\uD835[\uDFCE-\uDFFF]|\uD838[\uDD40-\uDD49\uDEF0-\uDEF9]|\uD83A[\uDCC7-\uDCCF\uDD50-\uDD59]|\uD83B[\uDC71-\uDCAB\uDCAD-\uDCAF\uDCB1-\uDCB4\uDD01-\uDD2D\uDD2F-\uDD3D]|\uD83C[\uDD00-\uDD0C]|\uD83E[\uDFF0-\uDFF9])/g, '0').replace(/(?:[A-Za-z\xAA\xB5\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0560-\u0588\u05D0-\u05EA\u05EF-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u0860-\u086A\u08A0-\u08B4\u08B6-\u08C7\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u09FC\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C60\u0C61\u0C80\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D04-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D54-\u0D56\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E86-\u0E8A\u0E8C-\u0EA3\u0EA5\u0EA7-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16F1-\u16F8\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1878\u1880-\u1884\u1887-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1C80-\u1C88\u1C90-\u1CBA\u1CBD-\u1CBF\u1CE9-\u1CEC\u1CEE-\u1CF3\u1CF5\u1CF6\u1CFA\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2183\u2184\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005\u3006\u3031-\u3035\u303B\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312F\u3131-\u318E\u31A0-\u31BF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\u9FFC\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6E5\uA717-\uA71F\uA722-\uA788\uA78B-\uA7BF\uA7C2-\uA7CA\uA7F5-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA8FE\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB69\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]|\uD800[\uDC00-\uDC0B\uDC0D-\uDC26\uDC28-\uDC3A\uDC3C\uDC3D\uDC3F-\uDC4D\uDC50-\uDC5D\uDC80-\uDCFA\uDE80-\uDE9C\uDEA0-\uDED0\uDF00-\uDF1F\uDF2D-\uDF40\uDF42-\uDF49\uDF50-\uDF75\uDF80-\uDF9D\uDFA0-\uDFC3\uDFC8-\uDFCF]|\uD801[\uDC00-\uDC9D\uDCB0-\uDCD3\uDCD8-\uDCFB\uDD00-\uDD27\uDD30-\uDD63\uDE00-\uDF36\uDF40-\uDF55\uDF60-\uDF67]|\uD802[\uDC00-\uDC05\uDC08\uDC0A-\uDC35\uDC37\uDC38\uDC3C\uDC3F-\uDC55\uDC60-\uDC76\uDC80-\uDC9E\uDCE0-\uDCF2\uDCF4\uDCF5\uDD00-\uDD15\uDD20-\uDD39\uDD80-\uDDB7\uDDBE\uDDBF\uDE00\uDE10-\uDE13\uDE15-\uDE17\uDE19-\uDE35\uDE60-\uDE7C\uDE80-\uDE9C\uDEC0-\uDEC7\uDEC9-\uDEE4\uDF00-\uDF35\uDF40-\uDF55\uDF60-\uDF72\uDF80-\uDF91]|\uD803[\uDC00-\uDC48\uDC80-\uDCB2\uDCC0-\uDCF2\uDD00-\uDD23\uDE80-\uDEA9\uDEB0\uDEB1\uDF00-\uDF1C\uDF27\uDF30-\uDF45\uDFB0-\uDFC4\uDFE0-\uDFF6]|\uD804[\uDC03-\uDC37\uDC83-\uDCAF\uDCD0-\uDCE8\uDD03-\uDD26\uDD44\uDD47\uDD50-\uDD72\uDD76\uDD83-\uDDB2\uDDC1-\uDDC4\uDDDA\uDDDC\uDE00-\uDE11\uDE13-\uDE2B\uDE80-\uDE86\uDE88\uDE8A-\uDE8D\uDE8F-\uDE9D\uDE9F-\uDEA8\uDEB0-\uDEDE\uDF05-\uDF0C\uDF0F\uDF10\uDF13-\uDF28\uDF2A-\uDF30\uDF32\uDF33\uDF35-\uDF39\uDF3D\uDF50\uDF5D-\uDF61]|\uD805[\uDC00-\uDC34\uDC47-\uDC4A\uDC5F-\uDC61\uDC80-\uDCAF\uDCC4\uDCC5\uDCC7\uDD80-\uDDAE\uDDD8-\uDDDB\uDE00-\uDE2F\uDE44\uDE80-\uDEAA\uDEB8\uDF00-\uDF1A]|\uD806[\uDC00-\uDC2B\uDCA0-\uDCDF\uDCFF-\uDD06\uDD09\uDD0C-\uDD13\uDD15\uDD16\uDD18-\uDD2F\uDD3F\uDD41\uDDA0-\uDDA7\uDDAA-\uDDD0\uDDE1\uDDE3\uDE00\uDE0B-\uDE32\uDE3A\uDE50\uDE5C-\uDE89\uDE9D\uDEC0-\uDEF8]|\uD807[\uDC00-\uDC08\uDC0A-\uDC2E\uDC40\uDC72-\uDC8F\uDD00-\uDD06\uDD08\uDD09\uDD0B-\uDD30\uDD46\uDD60-\uDD65\uDD67\uDD68\uDD6A-\uDD89\uDD98\uDEE0-\uDEF2\uDFB0]|\uD808[\uDC00-\uDF99]|\uD809[\uDC80-\uDD43]|[\uD80C\uD81C-\uD820\uD822\uD840-\uD868\uD86A-\uD86C\uD86F-\uD872\uD874-\uD879\uD880-\uD883][\uDC00-\uDFFF]|\uD80D[\uDC00-\uDC2E]|\uD811[\uDC00-\uDE46]|\uD81A[\uDC00-\uDE38\uDE40-\uDE5E\uDED0-\uDEED\uDF00-\uDF2F\uDF40-\uDF43\uDF63-\uDF77\uDF7D-\uDF8F]|\uD81B[\uDE40-\uDE7F\uDF00-\uDF4A\uDF50\uDF93-\uDF9F\uDFE0\uDFE1\uDFE3]|\uD821[\uDC00-\uDFF7]|\uD823[\uDC00-\uDCD5\uDD00-\uDD08]|\uD82C[\uDC00-\uDD1E\uDD50-\uDD52\uDD64-\uDD67\uDD70-\uDEFB]|\uD82F[\uDC00-\uDC6A\uDC70-\uDC7C\uDC80-\uDC88\uDC90-\uDC99]|\uD835[\uDC00-\uDC54\uDC56-\uDC9C\uDC9E\uDC9F\uDCA2\uDCA5\uDCA6\uDCA9-\uDCAC\uDCAE-\uDCB9\uDCBB\uDCBD-\uDCC3\uDCC5-\uDD05\uDD07-\uDD0A\uDD0D-\uDD14\uDD16-\uDD1C\uDD1E-\uDD39\uDD3B-\uDD3E\uDD40-\uDD44\uDD46\uDD4A-\uDD50\uDD52-\uDEA5\uDEA8-\uDEC0\uDEC2-\uDEDA\uDEDC-\uDEFA\uDEFC-\uDF14\uDF16-\uDF34\uDF36-\uDF4E\uDF50-\uDF6E\uDF70-\uDF88\uDF8A-\uDFA8\uDFAA-\uDFC2\uDFC4-\uDFCB]|\uD838[\uDD00-\uDD2C\uDD37-\uDD3D\uDD4E\uDEC0-\uDEEB]|\uD83A[\uDC00-\uDCC4\uDD00-\uDD43\uDD4B]|\uD83B[\uDE00-\uDE03\uDE05-\uDE1F\uDE21\uDE22\uDE24\uDE27\uDE29-\uDE32\uDE34-\uDE37\uDE39\uDE3B\uDE42\uDE47\uDE49\uDE4B\uDE4D-\uDE4F\uDE51\uDE52\uDE54\uDE57\uDE59\uDE5B\uDE5D\uDE5F\uDE61\uDE62\uDE64\uDE67-\uDE6A\uDE6C-\uDE72\uDE74-\uDE77\uDE79-\uDE7C\uDE7E\uDE80-\uDE89\uDE8B-\uDE9B\uDEA1-\uDEA3\uDEA5-\uDEA9\uDEAB-\uDEBB]|\uD869[\uDC00-\uDEDD\uDF00-\uDFFF]|\uD86D[\uDC00-\uDF34\uDF40-\uDFFF]|\uD86E[\uDC00-\uDC1D\uDC20-\uDFFF]|\uD873[\uDC00-\uDEA1\uDEB0-\uDFFF]|\uD87A[\uDC00-\uDFE0]|\uD87E[\uDC00-\uDE1D]|\uD884[\uDC00-\uDF4A])/g, 'X');
  }

  /**
   * attr returns e.getAttribute(name) but attr(e, "name") works even if e is a
   * <form><input name=name /></form>.
   * @param {HTMLElement} e
   * @param {string} name
   */
  function attr(e, name) {
    return HTMLElement.prototype.getAttribute.call(e, name);
  }

  /**
   * treeHasAttr returns true if e or any of its parents have the named attribute.
   * @param {HTMLElement} e
   * @param {string} name
   * @returns {boolean}
   */
  function treeHasAttr(e, name) {
    while (e && e.hasAttribute) {
      if (e.hasAttribute(name)) {
        return true;
      }
      e = e.parentNode;
    }
    return false;
  }

  /**
   * debounce returns a function that calls fn after delayMs of the function not
   * being called. Arguments from the latest invocation are used.
   *
   * @param {number} delayMs
   * @param {Function} fn
   */
  function debounce(delayMs, fn) {
    var wait;
    return function() {
      var args = Array.prototype.slice.call(arguments);
      if (wait) clearTimeout(wait);
      wait = setTimeout(function() {
        fn.apply(null, args);
      }, delayMs);
    };
  }

  /**
    * @param {object} [cfg]
    */
  function Ravelin(cfg) {
    cfg.Promise = cfg.Promise || Ravelin.Promise;
    this.core = new Core(cfg);
    this.track = new Track(this.core, cfg);
  }

  Ravelin.Promise = typeof Promise !== 'undefined' && Promise;

  return Ravelin;

}));
